import React, { useEffect, useMemo, useState } from "react";
import MessengerLayout         from "../../../layout/MessengerLayout";
import TicketFilters           from "./filters/TicketFilters";
import TicketKpis              from "./kpi/TicketKpis";
import PriorityQueues          from "./queues/PriorityQueues";
import TicketGrid              from "./grid/TicketGrid";
import TicketDrawer            from "./drawer/TicketDrawer";
import SlaPanel                from "./sla/SlaPanel";
import WorkflowSettings        from "./settings/WorkflowSettings";
import CategoriesSettings      from "./settings/CategoriesSettings";
import QueuesSettings          from "./settings/QueuesSettings";
import AssignmentRules         from "./settings/AssignmentRules";
import AutoEscalationRules     from "./settings/AutoEscalationRules";
import ComingSoonOverlay       from "../ComingSoonOverlay";
import NotificationTemplates   from "./settings/NotificationTemplates";
import BusinessHoursCalendar   from "./settings/BusinessHoursCalendar";
import AgentWorkloadPanel      from "./settings/AgentWorkloadPanel";
import TicketReports           from "./reports/TicketReports";
import { toast }               from "../../ui/Toast";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" });

const TABS = [
  { key: "overview",  label: "Overview"  },
  { key: "reports",   label: "Reports"   },
  { key: "settings",  label: "Settings"  },
];

export default function AdminTicketsPage() {
  const [tab,      setTab]      = useState("overview");
  const [filters,  setFilters]  = useState({ query:"", queue:"ALL", category:"ALL", priority:"ALL", status:"ALL", dept:"ALL", assignee:"ALL" });
  const [tickets,  setTickets]  = useState([]);
  const [kpis,     setKpis]     = useState(null);
  const [queues,   setQueues]   = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    if (tab !== "overview") return;

    const run = () => {
      setLoading(true); setError(null);
      Promise.all([
        fetch(`${API}/api/admin/tickets`,       { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API}/api/admin/tickets/kpis`,  { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API}/api/admin/tickets/queues`,{ headers: authHeaders() }).then(r => r.json()),
      ])
        .then(([t, k, q]) => {
          setTickets(Array.isArray(t) ? t : t?.tickets || t?.data || []);
          setKpis(k || null);
          setQueues(Array.isArray(q) ? q.map(x => x.name || x) : []);
        })
        .catch(e => setError(e.message))
        .finally(() => setLoading(false));
    };

    run();
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["ticket_raise"].includes(t)) {
        run();
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => window.removeEventListener("kira_action_complete", handleKiraAction);
  }, [tab]);

  const view = useMemo(() => tickets.filter(t => {
    if (filters.category !== "ALL" && t.category !== filters.category) return false;
    if (filters.priority !== "ALL" && t.priority !== filters.priority) return false;
    if (filters.status   !== "ALL" && t.status   !== filters.status)   return false;
    if (filters.dept     !== "ALL" && t.dept     !== filters.dept)     return false;
    if (filters.assignee !== "ALL" && t.assignee !== filters.assignee) return false;
    if (filters.query && !(`${t.id} ${t.subject} ${t.requester}`.toLowerCase().includes(filters.query.toLowerCase()))) return false;
    return true;
  }), [tickets, filters]);

  /* ─── Ticket Actions ─── */
  const apiPatch = (url, body) =>
    fetch(url, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(body) })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r; });

  const apiPost = (url, body = {}) =>
    fetch(url, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r; });

  const patchLocal = (id, patch) => setTickets(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));

  const assign = (id, who) => {
    patchLocal(id, { assignee: who });
    apiPatch(`${API}/api/admin/tickets/${id}`, { assignee: who })
      .then(() => toast.success("Assign successful"))
      .catch(() => toast.error("Failed to assign"));
  };
  const setStatus = (id, st) => {
    patchLocal(id, { status: st });
    apiPatch(`${API}/api/admin/tickets/${id}`, { status: st })
      .then(() => toast.success("Status updated"))
      .catch(() => toast.error("Failed to update status"));
  };
  const setPri = (id, p) => {
    patchLocal(id, { priority: p });
    apiPatch(`${API}/api/admin/tickets/${id}`, { priority: p })
      .then(() => toast.success("Priority updated"))
      .catch(() => toast.error("Failed to update priority"));
  };
  const addNote = (id, note) =>
    apiPost(`${API}/api/admin/tickets/${id}/notes`, { note, internal: true })
      .then(() => toast.success("Note added"))
      .catch(() => toast.error("Failed to add note"));
  const escalate = (id) => {
    patchLocal(id, { status: "Escalated" });
    apiPost(`${API}/api/admin/tickets/escalate/${id}`)
      .then(() => toast.success("Ticket escalated"))
      .catch(() => toast.error("Failed to escalate"));
  };
  // Hold / Reopen are only dispatched from TicketDrawer, which already
  // POSTs and toasts. Keep these as local-state-only updaters to avoid
  // duplicate API calls and duplicate toasts.
  const holdTicket   = (id) => patchLocal(id, { status: "On Hold" });
  const reopenTicket = (id) => patchLocal(id, { status: "Open"    });

  const approve = (id, who) => {
    patchLocal(id, { approvals: (tickets.find(t => t.id === id)?.approvals || []).filter(a => a !== who) });
    apiPost(`${API}/api/admin/tickets/${id}/approve`, { approver: who })
      .then(() => toast.success("Ticket approved"))
      .catch(() => toast.error("Failed to approve"));
  };

  const merge = async (ids) => {
    try {
      const res = await fetch(`${API}/api/admin/tickets/merge`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ ids }) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTickets(prev => prev.filter(t => !ids.slice(1).includes(t.id)));
      toast.success("Tickets merged");
    } catch (e) {
      setError(e.message);
      toast.error("Failed to merge");
    }
  };

  const split = (id) =>
    apiPost(`${API}/api/admin/tickets/${id}/split`)
      .then(() => toast.success("Ticket split"))
      .catch(() => toast.error("Failed to split ticket"));

  // Bulk actions — single toast for the whole batch.
  const bulkPatch = async (ids, patch) => {
    try {
      await Promise.all(ids.map(id => fetch(`${API}/api/admin/tickets/${id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(patch) }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); })));
      setTickets(prev => prev.map(t => ids.includes(t.id) ? { ...t, ...patch } : t));
      toast.success("Bulk action complete");
    } catch {
      toast.error("Bulk action failed");
    }
  };

  const bulkEscalate = async (ids) => {
    try {
      await Promise.all(ids.map(id => fetch(`${API}/api/admin/tickets/escalate/${id}`, { method: "POST", headers: authHeaders(), body: "{}" }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); })));
      setTickets(prev => prev.map(t => ids.includes(t.id) ? { ...t, status: "Escalated" } : t));
      toast.success("Bulk action complete");
    } catch {
      toast.error("Bulk action failed");
    }
  };
  const bulkClose    = (ids) => bulkPatch(ids, { status: "Closed"    });
  const bulkHold     = (ids) => bulkPatch(ids, { status: "On Hold"   });
  const bulkReopen   = (ids) => bulkPatch(ids, { status: "Open"      });

  const bulkReassign = async (ids, agent) => {
    try {
      const res = await fetch(`${API}/api/admin/tickets/bulk-reassign`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ ids, assignee: agent }) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTickets(prev => prev.map(t => ids.includes(t.id) ? { ...t, assignee: agent } : t));
      toast.success("Bulk action complete");
    } catch {
      toast.error("Bulk action failed");
    }
  };

  const bulkCategory = async (ids, category) => {
    try {
      const res = await fetch(`${API}/api/admin/tickets/bulk-category`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ ids, category }) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTickets(prev => prev.map(t => ids.includes(t.id) ? { ...t, category } : t));
      toast.success("Bulk action complete");
    } catch {
      toast.error("Bulk action failed");
    }
  };

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">Tickets</h1>
          <p className="text-white/40 text-sm mt-0.5">Manage support queues, SLA, escalation rules and reports</p>
        </div>

        {/* Tab nav */}
        <div className="flex gap-1 border-b border-white/5 pb-1">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`text-xs px-4 py-2 rounded-t-lg border-b-2 transition-colors ${
                tab === t.key ? "border-[#00ff90] text-[#00ff90]" : "border-transparent text-white/40 hover:text-white/70"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW ── */}
        {tab === "overview" && (
          <>
            <TicketFilters filters={filters} setFilters={setFilters} />
            <TicketKpis tickets={view} kpis={kpis} />
            {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}
            <PriorityQueues tickets={tickets} onPick={setSelected} onEscalate={escalate} />
            <TicketGrid
              rows={view}
              onOpen={setSelected}
              onAssign={assign}
              onMerge={merge}
              onSplit={split}
              onBulkEscalate={bulkEscalate}
              onBulkClose={bulkClose}
              onBulkHold={bulkHold}
              onBulkReopen={bulkReopen}
              onBulkReassign={bulkReassign}
              onBulkCategory={bulkCategory}
              assignees={queues}
            />
            <ComingSoonOverlay note="Agent workload + bulk reassign — coming soon">
              <AgentWorkloadPanel localTickets={tickets} assignees={queues} />
            </ComingSoonOverlay>
          </>
        )}

        {/* ── REPORTS ── */}
        {tab === "reports" && <TicketReports rows={tickets} />}

        {/* ── SETTINGS ── */}
        {tab === "settings" && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <SlaPanel />
              <QueuesSettings />
              <CategoriesSettings />
              <AssignmentRules />
            </div>
            <ComingSoonOverlay note="Auto-escalation / auto-close — coming soon">
              <AutoEscalationRules />
            </ComingSoonOverlay>
            <ComingSoonOverlay note="Business hours + on-call calendar — coming soon">
              <BusinessHoursCalendar />
            </ComingSoonOverlay>
            <NotificationTemplates />
            <div className="grid grid-cols-1 md:grid-cols-1 gap-5">
              <WorkflowSettings />
            </div>
          </div>
        )}
      </div>

      <TicketDrawer
        open={!!selected}
        ticket={selected}
        onClose={() => setSelected(null)}
        onAssign={assign}
        onSetStatus={setStatus}
        onSetPriority={setPri}
        onAddNote={addNote}
        onApprove={approve}
        onHold={holdTicket}
        onReopen={reopenTicket}
        assignees={queues}
      />
    </MessengerLayout>
  );
}